import { useAppActionComposable } from '@/composables/useAppActionComposable';
import { useAssetStore } from '@/stores/api/asset';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { cameraControls } from '@/threejs/core';
import { useDialog } from '@disguise/cloud-ui';
import { defineAsyncComponent } from 'vue';
import { useDataValidation } from './useDataValidation';

const CreateComment = defineAsyncComponent(() => import('@/components/dialogs/CreateComment.vue'));

export const useComments = () => {
  const dialog = useDialog();

  const cruxModuleStore = useCruxModuleStore();
  const assetStore = useAssetStore();
  const { sanitizeModuleData } = useDataValidation();
  const { handleApiResponse } = useAppActionComposable();

  const openNewCommentDialog = () => {
    dialog.open(CreateComment, {
      props: {
        dismissableMask: true,
        header: 'Create Comment',
        style: {
          width: '30vw',
        },
        breakpoints: {
          '640px': '90vw',
        },
        modal: true,
      },
      onClose: async (options) => {
        const { buttonType } = options.data || {};

        if (buttonType === 'Save') {
          const { name } = options.data || 'New Annotation';
          const response = await cruxModuleStore.addSceneModule(
            assetStore.currentAsset.latest_version.code,
            'annotation',
            {
              code: crypto.randomUUID(),
              text: options.data.name,
              position: {
                x: cameraControls.camera.position.x,
                y: cameraControls.camera.position.y,
                z: cameraControls.camera.position.z,
              },
              target: {
                x: cameraControls._target.x,
                y: cameraControls._target.y,
                z: cameraControls._target.z,
              },
              fov: cameraControls._zoom0 * 100,
            },
            name.length > 50 ? name.substring(0, 100) + '...' : name
          );
          const { error } = response;
          if (error) {
            handleApiResponse(error);
            return;
          }
        }
      },
    });
  };

  const navigateToComment = (comment) => {
    if (comment.spatial?.camera_target) {
      cameraControls.setTarget(
        comment.spatial.camera_target[0],
        comment.spatial.camera_target[1],
        comment.spatial.camera_target[2],
        true
      );
    }

    if (comment.spatial?.camera_position) {
      cameraControls.setPosition(
        comment.spatial.camera_position[0],
        comment.spatial.camera_position[1],
        comment.spatial.camera_position[2],
        true
      );
    }
  };

  const openEditCommentDialog = async (commentId) => {
    const comment = await cruxModuleStore.cruxModules.find((module) => module.code === commentId);
    if (!comment) return;

    dialog.open(CreateComment, {
      props: {
        dismissableMask: true,
        header: 'Edit Comment',
        style: {
          width: '30vw',
        },
        breakpoints: {
          '640px': '90vw',
        },
        modal: true,
      },
      data: {
        update: true,
        comment: comment,
      },
      onClose: async (options) => {
        if (options.data?.buttonType === 'Update') {
          const { name } = options.data || 'New Annotation';
          const payload = {
            ...comment,
            data: { ...comment.data, text: name },
            name: name.length > 25 ? name.substring(0, 100) + '...' : name,
          };
          const response = await cruxModuleStore.update(commentId, payload);
          const { error } = response;
          if (error) {
            handleApiResponse(error);
            return;
          }
          comment.data.text = name;
          comment.name = payload.name;
        } else if (options.data?.buttonType === 'Delete') {
          cruxModuleStore.destroy(commentId);
        }
      },
    });
  };

  return {
    openNewCommentDialog,
    openEditCommentDialog,
    navigateToComment,
  };
};
